import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bluetooth, Copy, ExternalLink, Lock } from "lucide-react";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast, toAbsoluteUrl } from "@/utils/helpers";
import type { DeviceV2, PatientV2 } from "../types";

const STATUS_OPTIONS = [
  { value: "deliveryReq", label: "Delivery Requested" },
  { value: "ordered", label: "Order Placed" },
  { value: "shipped", label: "In Transit" },
  { value: "delivered", label: "Delivered" },
  { value: "addressOnHold", label: "Address on Hold" },
  { value: "onHold", label: "On Hold" },
  { value: "denial", label: "Denial" },
  { value: "returned", label: "Order Returned" },
  { value: "firstReadingDone", label: "First Reading Done" },
  { value: "wrongAddressDelivered", label: "Wrong Address Delivered" },
];
const NEXT_STEP_OPTIONS = [
  { value: "deviceSetupPending", label: "Device Setup Pending" },
  { value: "deviceSetupCompleted", label: "Device Setup Completed" },
  { value: "other", label: "Other" },
  { value: "returnCompleted", label: "Return Completed" },
  { value: "returnPending", label: "Return Pending" },
];

type DeviceTypeOption = { value: string; label: string };

function normalizeDeviceTypeOption(item: unknown): DeviceTypeOption | null {
  if (typeof item === "string" && item.trim()) {
    return { value: item, label: item };
  }
  if (item && typeof item === "object") {
    const obj = item as Record<string, unknown>;
    const value = String(
      obj.value ?? obj.deviceType ?? obj.key ?? obj.code ?? obj._id ?? "",
    );
    if (!value) return null;
    const label = String(obj.label ?? obj.name ?? obj.title ?? value);
    return { value, label };
  }
  return null;
}

const CONNECTION_TYPE = "Bluetooth";

const EMPTY_FORM = {
  deviceType: "",
  imei: "",
  serialNumber: "",
  status: "",
  nextStep: "",
  trackingNumber: "",
  trackingUrl: "",
  expectedDateOfDelivery: "",
};

// function RequiredBadge() {
//   return (
//     <Badge
//       variant="outline"
//       className="border-blue-200 bg-blue-50 text-blue-700"
//     >
//       Required
//     </Badge>
//   );
// }

// function LockedBadge() {
//   return (
//     <Badge
//       variant="outline"
//       className="border-muted-foreground/20 bg-muted text-muted-foreground"
//     >
//       <Lock className="h-3 w-3" />
//       Locked
//     </Badge>
//   );
// }

function getPatientDisplayName(patient: PatientV2 | null): string {
  if (!patient) return "—";
  const middleInitial = patient.middleName
    ? `${patient.middleName.charAt(0)}.`
    : "";
  return (
    [patient.firstName, middleInitial, patient.lastName]
      .filter(Boolean)
      .join(" ") || "—"
  );
}

function mapDeviceToForm(device: DeviceV2): typeof EMPTY_FORM {
  return {
    deviceType: String(device.deviceType ?? ""),
    imei: String(device.imei ?? ""),
    serialNumber: String(device.serialNumber ?? ""),
    status: String(device.status ?? ""),
    nextStep: String(device.nextStep ?? ""),
    trackingNumber: String(device.trackingNumber ?? ""),
    trackingUrl: String(device.trackingUrl ?? ""),
    expectedDateOfDelivery: String(device.expectedDateOfDelivery ?? ""),
  };
}

export function AddDeviceModal({
  open,
  onOpenChange,
  patient,
  patientId,
  device,
  onDeviceAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: PatientV2 | null;
  patientId: string;
  device?: DeviceV2 | null;
  onDeviceAdded?: () => Promise<void>;
}) {
  const isEditMode = Boolean(device);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState<{
    deviceType?: string;
    status?: string;
    nextStep?: string;
  }>({});
  const [saving, setSaving] = useState(false);
  const [deviceTypeOptions, setDeviceTypeOptions] = useState<
    DeviceTypeOption[]
  >([]);
  const [deviceTypesLoading, setDeviceTypesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(device ? mapDeviceToForm(device) : EMPTY_FORM);
    setErrors({});
  }, [open, device]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      setDeviceTypesLoading(true);
      try {
        // Typed as `any`: some environments resolve a conflicting/stale
        // Axios response type here (e.g. mismatched @types/axios install)
        // that infers `data` as `{}` and drops `.finally()` from the
        // returned promise. async/await + try/finally sidesteps both —
        // native `await` doesn't require `.finally()` to exist, and this
        // explicit `any` stops the `{}` inference regardless of what's
        // installed.
        const response: any = await AxiosInstance().get(
          "/devices/bluetooth/device-types",
        );
        if (cancelled) return;
        const data = response?.data;
        console.log(
          "[AddDeviceModal] GET /devices/bluetooth/device-types",
          data,
        );
        const raw =
          data?.data?.deviceTypes ?? data?.data ?? data?.deviceTypes ?? [];
        const list = Array.isArray(raw)
          ? raw
              .map(normalizeDeviceTypeOption)
              .filter((item): item is DeviceTypeOption => item !== null)
          : [];
        setDeviceTypeOptions(list);
      } catch (error) {
        console.log("[AddDeviceModal] device-types fetch failed", error);
        if (!cancelled) {
          toast.error("Failed to load device types");
          setDeviceTypeOptions([]);
        }
      } finally {
        if (!cancelled) setDeviceTypesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setForm(EMPTY_FORM);
      setErrors({});
    }
  };

  const REQUIRED_FIELD_MESSAGES: Record<keyof typeof errors, string> = {
    deviceType: "Device type is required",
    status: "Status is required",
    nextStep: "Next step is required",
  };

  const validateField = (field: keyof typeof errors, value: string) => {
    const message = value ? undefined : REQUIRED_FIELD_MESSAGES[field];
    setErrors((prev) => ({ ...prev, [field]: message }));
    return message;
  };

  const hasBlockingErrors = Object.values(errors).some(Boolean);

  const handleCopyTrackingId = async () => {
    if (!form.trackingNumber) return;
    try {
      await navigator.clipboard.writeText(form.trackingNumber);
      toast.success("Tracking ID copied");
    } catch {
      toast.error("Failed to copy tracking ID");
    }
  };

  const handleSubmit = async () => {
    const deviceTypeError = validateField("deviceType", form.deviceType);
    const statusError = validateField("status", form.status);
    const nextStepError = validateField("nextStep", form.nextStep);
    if (deviceTypeError || statusError || nextStepError) {
      return;
    }
    if (!patientId) {
      toast.error("Patient ID is missing");
      return;
    }

    const basePayload = {
      deviceType: form.deviceType,
      status: form.status,
      nextStep: form.nextStep,
      imei: form.imei || undefined,
      serialNumber: form.serialNumber || undefined,
      trackingNumber: form.trackingNumber || undefined,
      trackingUrl: form.trackingUrl || undefined,
      expectedDateOfDelivery: form.expectedDateOfDelivery || undefined,
    };

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setSaving(true);
      // console.log(
      //   "[AddDeviceModal] auth token in localStorage:",
      //   localStorage.getItem("userdatatoken"),
      // );
      if (isEditMode) {
        const url = `/devices/bluetooth/${device?._id}`;
        console.log("[AddDeviceModal] PUT", url, basePayload);
        await AxiosInstance().put(url, basePayload);
        resolveFn("Device updated successfully");
      } else {
        const payload = {
          ...basePayload,
          patientId,
          connectionType: CONNECTION_TYPE,
        };
        console.log("[AddDeviceModal] POST", "/devices/bluetooth", payload);
        await AxiosInstance().post("/devices/bluetooth", payload);
        resolveFn("Device added successfully");
      }
      handleClose(false);
      await onDeviceAdded?.();
    } catch (error: unknown) {
      const typedError = error as {
        response?: { status?: number; data?: { message?: string } };
        config?: { url?: string; method?: string; baseURL?: string };
      };
      console.log("[AddDeviceModal] request failed", {
        status: typedError?.response?.status,
        data: typedError?.response?.data,
        fullUrl: `${typedError?.config?.baseURL ?? ""}${typedError?.config?.url ?? ""}`,
        method: typedError?.config?.method,
      });
      const message =
        typedError?.response?.data?.message ||
        (isEditMode ? "Failed to update device" : "Failed to add device");
      rejectFn(error);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="thin-invisible-scrollbar w-full gap-0 overflow-y-auto sm:max-w-md">
        <SheetHeader className="border-b">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-600 text-white">
              <Bluetooth className="h-4 w-4" />
            </div>
            <div>
              <SheetTitle>
                {isEditMode ? "Edit Device" : "Add Device"}
              </SheetTitle>
              <SheetDescription>
                Manage device status, logistics, tracking details &amp;
                identifiers
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="thin-invisible-scrollbar flex-1 space-y-5 overflow-y-auto p-4">
          <div className="flex items-center justify-between rounded-md border border-blue-100 bg-blue-50 px-3 py-3">
            <div>
              <p className="text-xs font-semibold uppercase text-blue-700">
                Assigned Patient
              </p>
              <p className="text-sm font-medium">
                {getPatientDisplayName(patient)}{" "}
                <span className="text-muted-foreground">
                  ({patient?.patientId ?? "—"})
                </span>
              </p>
            </div>
            <Badge variant="outline" className="capitalize">
              {patient?.status || "—"}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="add-device-type">
                  Device Type <span className="text-destructive">*</span>
                </Label>
                {/* <RequiredBadge /> */}
              </div>
              <Select
                value={form.deviceType}
                onValueChange={(value) => {
                  setForm((prev) => ({ ...prev, deviceType: value }));
                  setErrors((prev) => ({ ...prev, deviceType: undefined }));
                }}
                disabled={deviceTypesLoading}
              >
                <SelectTrigger
                  id="add-device-type"
                  className="w-full"
                  aria-invalid={Boolean(errors.deviceType)}
                  onBlur={() => validateField("deviceType", form.deviceType)}
                >
                  <SelectValue
                    placeholder={
                      deviceTypesLoading
                        ? "Loading device types…"
                        : "Select Device Type"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {deviceTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.deviceType ? (
                <p className="text-xs text-destructive">{errors.deviceType}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="add-device-connection-type">
                  Connection Type
                </Label>
                {/* <LockedBadge /> */}
              </div>
              <div className="relative">
                <Input
                  id="add-device-connection-type"
                  value={CONNECTION_TYPE}
                  disabled
                  className="pr-9 text-muted-foreground"
                />
                <Lock className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="add-device-imei">IMEI Number</Label>
                <span className="text-xs text-muted-foreground">
                  (Optional)
                </span>
              </div>
              <Input
                id="add-device-imei"
                value={form.imei}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, imei: e.target.value }))
                }
                placeholder="Enter IMEI number"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="add-device-serial">Serial Number</Label>
                <span className="text-xs text-muted-foreground">
                  (Optional)
                </span>
              </div>
              <Input
                id="add-device-serial"
                value={form.serialNumber}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    serialNumber: e.target.value,
                  }))
                }
                placeholder="Enter serial number"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="add-device-status">
                Status <span className="text-destructive">*</span>
              </Label>
              {/* <RequiredBadge /> */}
            </div>
            <Select
              value={form.status}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, status: value }));
                setErrors((prev) => ({ ...prev, status: undefined }));
              }}
            >
              <SelectTrigger
                id="add-device-status"
                className="w-full"
                aria-invalid={Boolean(errors.status)}
                onBlur={() => validateField("status", form.status)}
              >
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.status ? (
              <p className="text-xs text-destructive">{errors.status}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="add-device-next-step">
                Next Step <span className="text-destructive">*</span>
              </Label>
              {/* <RequiredBadge /> */}
            </div>
            <Select
              value={form.nextStep}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, nextStep: value }));
                setErrors((prev) => ({ ...prev, nextStep: undefined }));
              }}
            >
              <SelectTrigger
                id="add-device-next-step"
                className="w-full"
                aria-invalid={Boolean(errors.nextStep)}
                onBlur={() => validateField("nextStep", form.nextStep)}
              >
                <SelectValue placeholder="Select next step" />
              </SelectTrigger>
              <SelectContent>
                {NEXT_STEP_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.nextStep ? (
              <p className="text-xs text-destructive">{errors.nextStep}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="add-device-tracking-id">Tracking ID</Label>
              {/* <span className="text-xs text-muted-foreground">(Optional)</span> */}
            </div>
            <div className="relative">
              <Input
                id="add-device-tracking-id"
                value={form.trackingNumber}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    trackingNumber: e.target.value,
                  }))
                }
                placeholder="Enter tracking ID"
                className="pr-9"
              />
              <button
                type="button"
                onClick={handleCopyTrackingId}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Copy tracking ID"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="add-device-tracking-url">Tracking URL</Label>
              {/* <span className="text-xs text-muted-foreground">(Optional)</span> */}
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="add-device-tracking-url"
                value={form.trackingUrl}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    trackingUrl: e.target.value,
                  }))
                }
                placeholder="Enter tracking URL"
              />
              {form.trackingUrl ? (
                <a
                  href={toAbsoluteUrl(form.trackingUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex shrink-0 items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-medium text-blue-600 hover:underline"
                >
                  Google Track
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="add-device-expected-delivery">
                Expected Date of Delivery
              </Label>
              {/* <span className="text-xs text-muted-foreground">(Optional)</span> */}
            </div>
            <Input
              id="add-device-expected-delivery"
              type="date"
              value={form.expectedDateOfDelivery}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  expectedDateOfDelivery: e.target.value,
                }))
              }
            />
          </div>
        </div>

        <SheetFooter className="flex-row justify-end border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || hasBlockingErrors}>
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
